//src/pages/DocumentsFormPage.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Archive,
  Download,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Loader2,
  Search,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  downloadDocument,
  getDocumentStorageSummary,
  listDocuments,
} from "@/services/documents";
import type {
  DocumentFileType,
  DocumentItem,
  DocumentStorageSummary,
} from "@/types/documents";

const PAGE_SIZE = 30;

function formatBytes(bytes: number) {
  if (!bytes) return "0 MB";
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(2).replace(".", ",")} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(1).replace(".", ",")} KB`;
}

function formatDate(value: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function getFileMeta(document: DocumentItem) {
  const ext = (document.file_extension || document.file_name.split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png", ".jpg", ".jpeg", ".png"].includes(ext)) {
    return {
      label: "Imagem",
      icon: FileImage,
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
      accent: "bg-emerald-50 text-emerald-700",
    };
  }
  if (["xls", "xlsx", ".xls", ".xlsx"].includes(ext)) {
    return {
      label: "Excel",
      icon: FileSpreadsheet,
      badge: "bg-green-50 text-green-700 border-green-200",
      accent: "bg-green-50 text-green-700",
    };
  }
  if (["doc", "docx", ".doc", ".docx"].includes(ext)) {
    return {
      label: "Word",
      icon: FileText,
      badge: "bg-blue-50 text-blue-700 border-blue-200",
      accent: "bg-blue-50 text-blue-700",
    };
  }
  if (["pdf", ".pdf"].includes(ext)) {
    return {
      label: "PDF",
      icon: FileArchive,
      badge: "bg-red-50 text-red-700 border-red-200",
      accent: "bg-red-50 text-red-700",
    };
  }
  return {
    label: "Arquivo",
    icon: FileText,
    badge: "bg-slate-50 text-slate-700 border-slate-200",
    accent: "bg-slate-50 text-slate-700",
  };
}

function saveBlob(blob: Blob, filename?: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "documento";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function DocumentsFormPage() {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [storage, setStorage] = useState<DocumentStorageSummary | null>(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [fileType, setFileType] = useState<DocumentFileType>("all");
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const usedPercentage = Math.min(storage?.percentage || 0, 100);
  const hasNextPage = offset + PAGE_SIZE < total;
  const hasPreviousPage = offset > 0;

  const documentStats = useMemo(() => {
    const pdfCount = documents.filter((item) => getFileMeta(item).label === "PDF").length;
    const imageCount = documents.filter((item) => getFileMeta(item).label === "Imagem").length;
    return { pdfCount, imageCount };
  }, [documents]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [list, summary] = await Promise.all([
        listDocuments({ q: search, file_type: fileType, limit: PAGE_SIZE, offset }),
        getDocumentStorageSummary(),
      ]);
      setDocuments(list.items || []);
      setTotal(list.total || 0);
      setStorage(summary);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar documentos");
    } finally {
      setIsLoading(false);
    }
  }, [fileType, offset, search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadData();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadData]);

  async function handleDownload(documentItem: DocumentItem) {
    setDownloadingId(documentItem.id);
    try {
      const response = await downloadDocument(documentItem.id);
      saveBlob(response.blob, response.filename || documentItem.file_name);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao baixar documento");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentos"
        description="Consulte e baixe documentos disponíveis para sua empresa."
      />

      <div className="grid gap-4 xl:grid-cols-[1.35fr_0.65fr]">
        <Card className="overflow-hidden border-primary/10 bg-gradient-to-br from-primary/10 via-background to-background">
          <CardContent className="p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
                  <FolderOpen className="h-7 w-7" />
                </div>
                <div>
                  <Badge variant="outline" className="mb-3 bg-background/80">
                    <ShieldCheck className="mr-1 h-3.5 w-3.5 text-primary" />
                    Acesso somente leitura
                  </Badge>
                  <h2 className="text-xl font-semibold tracking-tight">Biblioteca de documentos</h2>
                  <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    Esta área é dedicada à consulta. Você pode pesquisar, filtrar e baixar arquivos, mas não possui permissão para enviar ou excluir documentos.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:min-w-[320px]">
                <div className="rounded-2xl border bg-background/80 p-4 shadow-sm">
                  <p className="text-xs text-muted-foreground">Documentos</p>
                  <strong className="mt-1 block text-2xl">{total}</strong>
                </div>
                <div className="rounded-2xl border bg-background/80 p-4 shadow-sm">
                  <p className="text-xs text-muted-foreground">Uso</p>
                  <strong className="mt-1 block text-2xl">{usedPercentage.toFixed(0)}%</strong>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Archive className="h-4 w-4 text-primary" />
              Armazenamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium">Uso atual</span>
                <span className="text-muted-foreground">{usedPercentage.toFixed(1).replace(".", ",")}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${usedPercentage}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-xl bg-muted/40 p-3">
                <span className="block text-muted-foreground">Utilizado</span>
                <strong className="mt-1 block text-foreground">{formatBytes(storage?.used_bytes || 0)}</strong>
              </div>
              <div className="rounded-xl bg-muted/40 p-3">
                <span className="block text-muted-foreground">Limite</span>
                <strong className="mt-1 block text-foreground">{storage?.limit_mb || 50} MB</strong>
              </div>
              <div className="rounded-xl bg-muted/40 p-3">
                <span className="block text-muted-foreground">Livre</span>
                <strong className="mt-1 block text-foreground">{formatBytes(storage?.remaining_bytes || 0)}</strong>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="text-base">Consultar documentos</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {documentStats.pdfCount} PDF(s), {documentStats.imageCount} imagem(ns) nesta página.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[280px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setOffset(0);
                  }}
                  placeholder="Buscar por título, arquivo ou descrição..."
                  className="pl-9"
                />
              </div>
              <Select
                value={fileType}
                onValueChange={(value) => {
                  setFileType(value as DocumentFileType);
                  setOffset(0);
                }}
              >
                <SelectTrigger className="w-full sm:w-[190px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="word">Word</SelectItem>
                  <SelectItem value="excel">Excel</SelectItem>
                  <SelectItem value="image">Imagens</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Carregando documentos...
            </div>
          ) : documents.length === 0 ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed bg-muted/20 text-center">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-3 font-semibold">Nenhum documento encontrado</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Ajuste os filtros de busca ou aguarde um usuário com permissão de gestão enviar documentos.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {documents.map((documentItem) => {
                const meta = getFileMeta(documentItem);
                const Icon = meta.icon;
                const isDownloading = downloadingId === documentItem.id;

                return (
                  <div
                    key={documentItem.id}
                    className="group grid gap-4 rounded-2xl border bg-card p-4 transition-all hover:border-primary/30 hover:shadow-sm lg:grid-cols-[1fr_auto] lg:items-center"
                  >
                    <div className="flex min-w-0 items-start gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${meta.accent}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="break-words text-sm font-semibold leading-6">{documentItem.title || documentItem.file_name}</h3>
                          <Badge variant="outline" className={meta.badge}>{meta.label}</Badge>
                        </div>
                        <p className="mt-1 break-all text-xs text-muted-foreground">
                          Arquivo original: {documentItem.original_file_name || documentItem.file_name}
                        </p>
                        {documentItem.description ? (
                          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{documentItem.description}</p>
                        ) : null}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <span>{formatBytes(documentItem.file_size_bytes)}</span>
                          <span>Enviado em {formatDate(documentItem.uploaded_at)}</span>
                          {documentItem.uploaded_by_name ? <span>Por {documentItem.uploaded_by_name}</span> : null}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" onClick={() => handleDownload(documentItem)} disabled={isDownloading}>
                        {isDownloading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="mr-2 h-4 w-4" />
                        )}
                        Baixar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex flex-col gap-3 border-t pt-4 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>
              Exibindo {documents.length} de {total} documento(s)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPreviousPage || isLoading}
                onClick={() => setOffset((value) => Math.max(value - PAGE_SIZE, 0))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!hasNextPage || isLoading}
                onClick={() => setOffset((value) => value + PAGE_SIZE)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
